import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from 'configs/EnvironmentConfig';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import { showSoftDeleteConflict } from 'store/slices/softDeleteSlice';

const buildBaseUrl = () => `${env.API_ENDPOINT_URL}${env.API_CLIENT_URL}`;

const rawBaseQuery = fetchBaseQuery({
	baseUrl: buildBaseUrl(),
	prepareHeaders: headers => {
		const token = localStorage.getItem(AUTH_TOKEN);
		headers.set('Content-Type', 'application/json');
		headers.set('Accept', 'application/json');
		// Honor explicit 'Authorization: public' if already set by endpoint
		if (token && headers.get('Authorization') !== 'public') {
			headers.set('Authorization', `Bearer ${token}`);
		}
		return headers;
	},
});

const toFullUrl = url => {
	if (!url) return url;
	const base = buildBaseUrl();
	return url.startsWith('http') ? url : `${base}${url}`;
};

const parseEntityId = url => {
	try {
		const clean = url.split('?')[0];
		const parts = clean.split('/');
		const last = parts[parts.length - 1];
		return isNaN(Number(last)) ? null : last;
	} catch (_) {
		return null;
	}
};

const buildRetryHeaders = () => {
	const token = localStorage.getItem(AUTH_TOKEN);
	const headers = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
	};
	if (token) headers.Authorization = `Bearer ${token}`;
	return headers;
};

const normalizeArgs = args => {
	if (typeof args === 'string') {
		return { url: args, method: 'GET' };
	}
	return args || {};
};

// In-memory pending registry to resolve baseQuery after user confirms
const pendingRequests = new Map(); // requestId -> { request, resolve, reject }

export const confirmSoftDelete = async requestId => {
	const pending = pendingRequests.get(requestId);
	if (!pending) return;
	const { request, resolve } = pending;
	const urlHasQuery = request.url.includes('?');
	const separator = urlHasQuery ? '&' : '?';
	const forcedUrl = `${request.url}${separator}force=true`;
	try {
		const res = await fetch(forcedUrl, {
			method: request.method || 'PUT',
			headers: request.headers || buildRetryHeaders(),
			body: request.body ?? undefined,
		});
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			resolve({ error: { status: res.status, data } });
		} else {
			const data = await res.json().catch(() => ({}));
			resolve({ data });
		}
	} catch (e) {
		resolve({ error: e });
	} finally {
		pendingRequests.delete(requestId);
	}
};

export const cancelSoftDelete = requestId => {
	const pending = pendingRequests.get(requestId);
	if (!pending) return;
	const { reject } = pending;
	reject({
		error: { status: 409, data: { message: 'User cancelled force delete' } },
	});
	pendingRequests.delete(requestId);
};

const baseQueryWith409 = async (args, api, extraOptions) => {
	const result = await rawBaseQuery(args, api, extraOptions);
	if (result && result.error && result.error.status === 409) {
		const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		const n = normalizeArgs(args);
		const url = toFullUrl(n.url);
		const method = (n.method || 'GET').toUpperCase();
		const body = n.body
			? typeof n.body === 'string'
				? n.body
				: JSON.stringify(n.body)
			: undefined;
		const headers = buildRetryHeaders();
		const entityId = parseEntityId(n.url || '');
		const conflict = result.error.data?.error || null;

		// Create deferred
		let resolve;
		let reject;
		const promise = new Promise((res, rej) => {
			resolve = res;
			reject = rej;
		});
		pendingRequests.set(requestId, {
			request: { url, method, body, headers },
			resolve,
			reject,
		});

		api.dispatch(
			showSoftDeleteConflict({
				requestId,
				request: { url, method, body, headers },
				entityId,
				conflict,
			})
		);

		return promise;
	}
	return result;
};

export default baseQueryWith409;
